import { ChangeDetectorRef } from '@angular/core';

import { deepEqual } from 'fast-equals';
import { BehaviorSubject, Observable } from 'rxjs';

import { deepFreeze } from './deep-freeze';

export interface RtStatefulComponent<State> {
  rtStateWillInit?: (initialState: State) => void;

  rtStateWillChange?: (prevState: State, nextState: State) => void;
}

// Extend this class when you want your component to have an internal state.
// (a state which will be controlled by the component itself
//  and on which the component's template/children is dependent).
//
// Usage example:
// ```
// type State = {
//   expanded: boolean
// };
//
// export class MyComponent extends RtStatefulComponent<State> {
//   constructor(protected cdRef: ChangeDetectorRef) {
//     super(cdRef);
//     this.createState({
//       expanded: false
//     });
//   }
//
//   // usage example: accessing and changing state
//   public handleExpandedChange(nextExpanded: boolean = !this.state.expanded): void {
//     this.setState({ expanded: nextExpanded });
//   }
// }
// ```

export class RtStatefulComponent<State> {
  public static immutableState: boolean = false;

  protected state$: Observable<State>;

  private __state$: BehaviorSubject<State>;
  private useDeepEqualCheck: boolean = true;

  constructor(protected cdRef: ChangeDetectorRef) {}

  public get state(): State {
    return this.__state$.getValue();
  }

  protected createState(
    initialState: State,
    { useDeepEqualCheck }: { useDeepEqualCheck: boolean } = { useDeepEqualCheck: true },
  ): void {
    this.useDeepEqualCheck = useDeepEqualCheck;
    if (this.rtStateWillInit) {
      this.rtStateWillInit(initialState);
    }

    this.__state$ = new BehaviorSubject<State>(initialState);
    this.state$ = this.__state$.asObservable();
  }

  protected setState(nextState: State, detectChanges: boolean = true): void {
    if (this.useDeepEqualCheck && deepEqual(nextState, this.state)) {
      return;
    }

    const previousState = this.state;
    if (this.rtStateWillChange) {
      this.rtStateWillChange(previousState, nextState);
    }

    this.__state$.next(RtStatefulComponent.immutableState ? deepFreeze(nextState) : nextState);

    if (detectChanges) {
      this.cdRef.detectChanges();
    }
  }
}
